/**
 * Zustand store for particle effect settings.
 * Covers thruster effects and planet atmosphere effects.
 * Settings can be exported/imported as JSON for sharing presets.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ---- Effect type enums ----

export const ThrusterEffectType = {
  PLASMA_EXHAUST: 'plasma_exhaust',
} as const;

export type ThrusterEffectTypeValue = (typeof ThrusterEffectType)[keyof typeof ThrusterEffectType];

export const AtmosphereEffectType = {
  DEFAULT: 'default',
} as const;

export type AtmosphereEffectTypeValue = (typeof AtmosphereEffectType)[keyof typeof AtmosphereEffectType];

const VALID_THRUSTER_EFFECTS: readonly string[] = [ThrusterEffectType.PLASMA_EXHAUST];
const VALID_ATMOSPHERE_EFFECTS: readonly string[] = [AtmosphereEffectType.DEFAULT];

// ---- Settings types ----

export interface ThrusterSettings {
  forward: string;
  back: string;
  side: string;
}

export interface PlanetAtmosphereSettings {
  terran: string;
  arid: string;
  ocean: string;
  tundra: string;
  volcanic: string;
  gas: string;
  barren: string;
  gaia: string;
}

export interface ParticleSettings {
  thrusters: ThrusterSettings;
  planetAtmospheres: PlanetAtmosphereSettings;
}

// ---- Default values ----

export const DEFAULT_PARTICLE_SETTINGS: ParticleSettings = {
  thrusters: {
    forward: ThrusterEffectType.PLASMA_EXHAUST,
    back: ThrusterEffectType.PLASMA_EXHAUST,
    side: ThrusterEffectType.PLASMA_EXHAUST,
  },
  planetAtmospheres: {
    terran: AtmosphereEffectType.DEFAULT,
    arid: AtmosphereEffectType.DEFAULT,
    ocean: AtmosphereEffectType.DEFAULT,
    tundra: AtmosphereEffectType.DEFAULT,
    volcanic: AtmosphereEffectType.DEFAULT,
    gas: AtmosphereEffectType.DEFAULT,
    barren: AtmosphereEffectType.DEFAULT,
    gaia: AtmosphereEffectType.DEFAULT,
  },
};

// ---- Validation ----

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

const THRUSTER_KEYS: ReadonlyArray<keyof ThrusterSettings> = ['forward', 'back', 'side'];
const ATMOSPHERE_KEYS: ReadonlyArray<keyof PlanetAtmosphereSettings> = [
  'terran', 'arid', 'ocean', 'tundra', 'volcanic', 'gas', 'barren', 'gaia',
];

function validateThrusters(v: unknown): v is ThrusterSettings {
  if (!isObject(v)) return false;
  for (const key of THRUSTER_KEYS) {
    if (typeof v[key] !== 'string' || !VALID_THRUSTER_EFFECTS.includes(v[key] as string)) return false;
  }
  return true;
}

function validateAtmospheres(v: unknown): v is PlanetAtmosphereSettings {
  if (!isObject(v)) return false;
  for (const key of ATMOSPHERE_KEYS) {
    if (typeof v[key] !== 'string' || !VALID_ATMOSPHERE_EFFECTS.includes(v[key] as string)) return false;
  }
  return true;
}

/**
 * Validate a complete particle settings object. Returns null on success, error string on failure.
 */
export function validateParticleSettings(data: unknown): string | null {
  if (!isObject(data)) return 'Particle settings must be an object';
  if (!('thrusters' in data)) return 'Missing section: thrusters';
  if (!('planetAtmospheres' in data)) return 'Missing section: planetAtmospheres';
  if (!validateThrusters(data.thrusters)) return 'Invalid settings for: thrusters';
  if (!validateAtmospheres(data.planetAtmospheres)) return 'Invalid settings for: planetAtmospheres';
  return null;
}

// ---- Store ----

interface ParticleState {
  settings: ParticleSettings;

  updateThruster: (key: keyof ThrusterSettings, value: string) => void;
  updatePlanetAtmosphere: (biome: keyof PlanetAtmosphereSettings, value: string) => void;
  resetToDefaults: () => void;
  importSettings: (settings: ParticleSettings) => void;
  exportSettings: () => string;
  applySettings: (settings: ParticleSettings) => void;
}

export const useParticleStore = create<ParticleState>()(
  persist(
    (set, get) => ({
      settings: { ...DEFAULT_PARTICLE_SETTINGS },

      updateThruster: (key, value) => set((state) => ({
        settings: {
          ...state.settings,
          thrusters: { ...state.settings.thrusters, [key]: value },
        },
      })),

      updatePlanetAtmosphere: (biome, value) => set((state) => ({
        settings: {
          ...state.settings,
          planetAtmospheres: { ...state.settings.planetAtmospheres, [biome]: value },
        },
      })),

      resetToDefaults: () => set({
        settings: { ...DEFAULT_PARTICLE_SETTINGS },
      }),

      importSettings: (settings) => set({
        settings: { ...settings },
      }),

      exportSettings: () => {
        return JSON.stringify(get().settings, null, 2);
      },

      applySettings: (settings) => set({
        settings: { ...settings },
      }),
    }),
    {
      name: 'warmancer-particles-v1',
      partialize: (state) => ({ settings: state.settings }),
      merge: (persisted, current) => {
        const persistedState = persisted as { settings?: Partial<ParticleSettings> } | undefined;
        if (!persistedState?.settings) return current;
        const merged = { ...current };
        // Validate persisted data before applying
        const error = validateParticleSettings(persistedState.settings);
        if (error) return current;
        merged.settings = persistedState.settings as ParticleSettings;
        return merged;
      },
    },
  ),
);
